import { Container, Graphics, Text, TextStyle } from 'pixi.js-legacy';
import { CONFIG } from '../config';
import { GameState } from '../core/Game';
import { PixiRenderer } from '../pixi/PixiRenderer';
import { BackgroundRenderer } from '../pixi/ui/BackgroundRenderer';

interface BGMTrackData {
    id: string;
    title: string;
}

interface SoundManagerLike {
    setSfxEnabled(enabled: boolean): void;
    setBgmEnabled(enabled: boolean): void;
    playBGM(): void;
    stopBGM(): void;
    setSelectedBGMTrack(trackId: string): void;
    getSelectedBGMTrack(): string;
    getAllBGMTracks(): BGMTrackData[];
}

interface InputManagerLike {
    cancelTouchConfirm(): void;
    isButtonPressed(button: string): boolean;
}

interface AchievementManagerLike {
    isUnlocked(id: string): boolean;
}

interface BackgroundState {
    hue: number;
    angle: number;
}

interface GameLike {
    getRenderer(): PixiRenderer;
    getSoundManager(): SoundManagerLike;
    getInputManager(): InputManagerLike;
    getAchievementManager(): AchievementManagerLike;
    getBackgroundState(): BackgroundState;
    setState(state: GameState): void;
}

type Difficulty = 'easy' | 'normal' | 'hard';
type BgStyle = 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid';

interface Options {
    difficulty: Difficulty;
    bgStyle: BgStyle;
    bigballMode: boolean;
    sfx: boolean;
    bgm: boolean;
    bgmTrack: string;
}

interface MenuItem {
    key: string;
    label: string;
    type: 'toggle' | 'select' | 'button';
    values?: string[];
    labels?: string[];
    locked?: boolean;
    hidden?: boolean;
}

export class OptionsScreen {
    private game: GameLike;
    private starAngle: number;
    private options: Options;
    private menuItems: MenuItem[];
    private selectedIndex: number;
    private clickHandler: ((e: MouseEvent) => void) | null;
    private touchEndHandler: ((e: TouchEvent) => void) | null;

    // PixiJS
    private pixiRenderer: PixiRenderer | null = null;
    private container: Container | null = null;
    private backgroundRenderer: BackgroundRenderer | null = null;
    private titleText: Text | null = null;
    private menuTexts: Text[] = [];
    private starGraphics: Graphics | null = null;

    constructor(game: GameLike) {
        this.game = game;
        this.starAngle = 0;

        this.options = this.loadOptions();

        this.menuItems = [
            { key: 'sfx', label: 'SFX', type: 'toggle' },
            { key: 'bgm', label: 'BGM', type: 'toggle' },
            { key: 'bgmTrack', label: 'Music', type: 'select', values: ['Dream Veil'], labels: ['Dream Veil'] },
            { key: 'difficulty', label: 'Difficulty', type: 'select', values: ['normal'], labels: ['Normal'] },
            { key: 'bigballMode', label: 'BigBall', type: 'toggle' },
            { key: 'bgStyle', label: 'BG Style', type: 'select', values: ['stripe', 'diamond', 'triangle', 'sine', 'hexagon', 'grid'], labels: ['Stripe', 'Diamond', 'Triangle', 'Sine', 'Hexagon', 'Grid'], hidden: true },
            { key: 'back', label: 'Back', type: 'button' }
        ];

        this.selectedIndex = 0;
        this.clickHandler = null;
        this.touchEndHandler = null;
    }

    setPixiRenderer(renderer: PixiRenderer): void {
        this.pixiRenderer = renderer;
    }

    getUnlockedDifficulties(): { values: string[]; labels: string[] } {
        const achievementManager = this.game.getAchievementManager();
        const difficulties = { values: ['normal'], labels: ['Normal'] };

        if (achievementManager.isUnlocked('death_3')) {
            difficulties.values.unshift('easy');
            difficulties.labels.unshift('Easy');
        }

        if (achievementManager.isUnlocked('evolve_max')) {
            difficulties.values.push('hard');
            difficulties.labels.push('Hard');
        }

        return difficulties;
    }

    isBigballUnlocked(): boolean {
        const achievementManager = this.game.getAchievementManager();
        return achievementManager.isUnlocked('score_1000');
    }

    getUnlockedBGMTracks(): { values: string[]; labels: string[] } {
        const achievementManager = this.game.getAchievementManager();
        const tracks = { values: ['Dream Veil'], labels: ['Dream Veil'] };

        if (achievementManager.isUnlocked('bgm_memorial_city')) {
            tracks.values.push('A Memorial City');
            tracks.labels.push('A Memorial City');
        }

        if (achievementManager.isUnlocked('bgm_neon_lights')) {
            tracks.values.push('Neon Lights Rendezvous');
            tracks.labels.push('Neon Lights Rendezvous');
        }

        return tracks;
    }

    getVisibleMenuItems(): MenuItem[] {
        return this.menuItems.filter(item => !item.hidden);
    }

    updateDifficultyMenu(): void {
        const unlocked = this.getUnlockedDifficulties();
        const difficultyItem = this.menuItems.find(item => item.key === 'difficulty');
        if (difficultyItem) {
            difficultyItem.values = unlocked.values;
            difficultyItem.labels = unlocked.labels;

            if (!unlocked.values.includes(this.options.difficulty)) {
                this.options.difficulty = 'normal';
                this.saveOptions();
            }

            difficultyItem.locked = unlocked.values.length <= 1;
        }

        const bigballItem = this.menuItems.find(item => item.key === 'bigballMode');
        if (bigballItem) {
            bigballItem.locked = !this.isBigballUnlocked();
            if (bigballItem.locked && this.options.bigballMode) {
                this.options.bigballMode = false;
                this.saveOptions();
            }
        }

        const unlockedTracks = this.getUnlockedBGMTracks();
        const bgmTrackItem = this.menuItems.find(item => item.key === 'bgmTrack');
        if (bgmTrackItem) {
            bgmTrackItem.values = unlockedTracks.values;
            bgmTrackItem.labels = unlockedTracks.labels;

            if (!unlockedTracks.values.includes(this.options.bgmTrack)) {
                this.options.bgmTrack = 'Dream Veil';
                this.saveOptions();
            }

            bgmTrackItem.locked = unlockedTracks.values.length <= 1;
        }
    }

    loadOptions(): Options {
        try {
            const saved = localStorage.getItem(CONFIG.OPTIONS.STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved) as Options;
            }
        } catch (e) {
            console.warn('Failed to load options:', e);
        }
        return {
            difficulty: CONFIG.OPTIONS.DIFFICULTY as Difficulty,
            bgStyle: CONFIG.OPTIONS.BG_STYLE as BgStyle,
            bigballMode: CONFIG.OPTIONS.BIGBALL_MODE,
            sfx: CONFIG.OPTIONS.SFX_ENABLED,
            bgm: CONFIG.OPTIONS.BGM_ENABLED,
            bgmTrack: CONFIG.OPTIONS.BGM_TRACK
        };
    }

    saveOptions(): void {
        try {
            localStorage.setItem(CONFIG.OPTIONS.STORAGE_KEY, JSON.stringify(this.options));
        } catch (e) {
            console.warn('Failed to save options:', e);
        }
    }

    init(): void {
        this.starAngle = 0;
        this.updateDifficultyMenu();
        const visibleItems = this.getVisibleMenuItems();
        if (this.selectedIndex >= visibleItems.length) {
            this.selectedIndex = 0;
        }
        this.setupClickListener();
        this.initPixiUI();
    }

    private initPixiUI(): void {
        if (!this.pixiRenderer) return;

        const layers = this.pixiRenderer.getLayers();
        this.container = new Container();
        layers.ui.addChild(this.container);

        // Background
        this.backgroundRenderer = new BackgroundRenderer(layers.background);

        const width = this.pixiRenderer.getCanvasWidth();
        const height = this.pixiRenderer.getCanvasHeight();

        // Title text
        const titleStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.SUBTITLE_SIZE * 1.5,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });
        this.titleText = new Text('OPTIONS', titleStyle);
        this.titleText.anchor.set(0.5);
        this.titleText.position.set(width / 2, height / 5);
        this.container.addChild(this.titleText);

        // Menu items
        const buttonFontSize = CONFIG.UI.SUBTITLE_SIZE * 0.7;
        const buttonStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: buttonFontSize,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });

        const visibleItems = this.getVisibleMenuItems();
        const startY = height / 3;
        const itemHeight = 80;

        this.menuTexts = [];
        visibleItems.forEach((item, index) => {
            const y = startY + index * itemHeight;
            const text = new Text(this.getMenuItemText(item), buttonStyle.clone());
            text.anchor.set(0.5);
            text.position.set(width / 2, y);
            this.container!.addChild(text);
            this.menuTexts.push(text);
        });

        // Selection star
        this.starGraphics = new Graphics();
        this.container.addChild(this.starGraphics);
    }

    private getMenuItemText(item: MenuItem): string {
        if (item.locked) {
            return '??? ???';
        }

        if (item.type === 'toggle') {
            const key = item.key as keyof Options;
            const value = this.options[key] ? 'ON' : 'OFF';
            return `${item.label} ${value}`;
        } else if (item.type === 'select' && item.values && item.labels) {
            const key = item.key as keyof Options;
            const currentValue = this.options[key] as string;
            const currentIndex = item.values.indexOf(currentValue);
            const label = item.labels[currentIndex] || currentValue;
            return `${item.label} ${label}`;
        }
        return item.label;
    }

    setupClickListener(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) return;

        this.clickHandler = (e: MouseEvent) => this.handleInput(e.clientX, e.clientY, e.target as HTMLElement);
        this.touchEndHandler = (e: TouchEvent) => {
            if (e.changedTouches.length > 0) {
                const touch = e.changedTouches[0];
                this.handleInput(touch.clientX, touch.clientY, e.target as HTMLElement);
            }
        };
        canvas.addEventListener('click', this.clickHandler);
        canvas.addEventListener('touchend', this.touchEndHandler);
    }

    handleInput(clientX: number, clientY: number, target: HTMLElement): void {
        const renderer = this.game.getRenderer();
        const rect = target.getBoundingClientRect();
        const scaleX = renderer.getCanvasWidth() / rect.width;
        const scaleY = renderer.getCanvasHeight() / rect.height;
        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        const width = renderer.getCanvasWidth();
        const height = renderer.getCanvasHeight();
        const startY = height / 3;
        const itemHeight = 80;
        const visibleItems = this.getVisibleMenuItems();

        for (let i = 0; i < visibleItems.length; i++) {
            const itemY = startY + i * itemHeight;
            if (y >= itemY - 30 && y <= itemY + 30 && x >= width / 2 - 150 && x <= width / 2 + 150) {
                if (visibleItems[i].locked) {
                    return;
                }
                this.selectedIndex = i;
                this.activateVisibleMenuItem(i);
                return;
            }
        }
    }

    activateVisibleMenuItem(index: number): void {
        const visibleItems = this.getVisibleMenuItems();
        const item = visibleItems[index];

        if (item.key === 'back') {
            this.saveOptions();
            this.game.setState(GameState.TITLE);
            return;
        }

        if (item.locked) {
            return;
        }

        if (item.type === 'toggle') {
            const key = item.key as keyof Options;
            (this.options[key] as boolean) = !this.options[key];
            this.applyOption(item.key);
        } else if (item.type === 'select' && item.values && item.labels) {
            const key = item.key as keyof Options;
            const currentValue = this.options[key] as string;
            const currentIndex = item.values.indexOf(currentValue);
            const nextIndex = (currentIndex + 1) % item.values.length;
            (this.options[key] as string) = item.values[nextIndex];
            this.applyOption(item.key);
        }

        this.saveOptions();
    }

    applyOption(key: string): void {
        const soundManager = this.game.getSoundManager();

        if (key === 'sfx') {
            soundManager.setSfxEnabled(this.options.sfx);
        } else if (key === 'bgm') {
            soundManager.setBgmEnabled(this.options.bgm);
            if (this.options.bgm) {
                soundManager.playBGM();
            } else {
                soundManager.stopBGM();
            }
        } else if (key === 'bgmTrack') {
            this.saveOptions();
            soundManager.setSelectedBGMTrack(this.options.bgmTrack);
            if (this.options.bgm) {
                soundManager.stopBGM();
                soundManager.playBGM();
            }
        }
    }

    findNextUnlockedIndex(currentIndex: number, direction: number): number {
        const visibleItems = this.getVisibleMenuItems();
        let nextIndex = currentIndex;

        for (let i = 0; i < visibleItems.length; i++) {
            nextIndex = (nextIndex + direction + visibleItems.length) % visibleItems.length;
            if (!visibleItems[nextIndex].locked) {
                return nextIndex;
            }
        }
        return currentIndex;
    }

    update(deltaTime: number): void {
        this.starAngle += deltaTime * 3;

        const input = this.game.getInputManager();
        input.cancelTouchConfirm();

        if (input.isButtonPressed('up')) {
            this.selectedIndex = this.findNextUnlockedIndex(this.selectedIndex, -1);
        }
        if (input.isButtonPressed('down')) {
            this.selectedIndex = this.findNextUnlockedIndex(this.selectedIndex, 1);
        }
        if (input.isButtonPressed('confirm')) {
            this.activateVisibleMenuItem(this.selectedIndex);
        }
        if (input.isButtonPressed('cancel')) {
            this.saveOptions();
            this.game.setState(GameState.TITLE);
        }
    }

    private drawRotatingStar(g: Graphics, x: number, y: number, size: number = 10): void {
        g.clear();
        g.lineStyle(2, 0xFFFFFF);
        g.position.set(x, y);
        g.rotation = this.starAngle;

        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            g.moveTo(0, 0);
            g.lineTo(Math.cos(angle) * size, Math.sin(angle) * size);
        }
    }

    render(): void {
        if (!this.pixiRenderer || !this.container) return;

        const renderer = this.game.getRenderer();
        const width = renderer.getCanvasWidth();
        const height = renderer.getCanvasHeight();
        const bg = this.game.getBackgroundState();

        // Update background
        if (this.backgroundRenderer) {
            this.backgroundRenderer.render(bg, width, height);
        }

        // Update title position
        if (this.titleText) {
            this.titleText.position.set(width / 2, height / 5);
        }

        // Update menu items
        const visibleItems = this.getVisibleMenuItems();
        const startY = height / 3;
        const itemHeight = 80;

        visibleItems.forEach((item, index) => {
            const text = this.menuTexts[index];
            if (text) {
                const y = startY + index * itemHeight;
                text.position.set(width / 2, y);
                text.text = this.getMenuItemText(item);

                const isLocked = item.locked;
                const isSelected = index === this.selectedIndex;

                if (isLocked) {
                    text.alpha = 0.25;
                } else {
                    text.alpha = isSelected ? 1 : 0.5;
                }
            }
        });

        // Update selection star
        if (this.starGraphics && this.selectedIndex < this.menuTexts.length) {
            const selectedText = this.menuTexts[this.selectedIndex];
            const visibleItem = visibleItems[this.selectedIndex];

            if (selectedText && !visibleItem?.locked) {
                const starX = width / 2 - selectedText.width / 2 - 25;
                const starY = selectedText.y;
                this.drawRotatingStar(this.starGraphics, starX, starY);
                this.starGraphics.visible = true;
            } else {
                this.starGraphics.visible = false;
            }
        }
    }

    cleanup(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (canvas) {
            if (this.clickHandler) {
                canvas.removeEventListener('click', this.clickHandler);
                this.clickHandler = null;
            }
            if (this.touchEndHandler) {
                canvas.removeEventListener('touchend', this.touchEndHandler);
                this.touchEndHandler = null;
            }
        }

        if (this.backgroundRenderer) {
            this.backgroundRenderer.destroy();
            this.backgroundRenderer = null;
        }

        if (this.container) {
            this.container.destroy({ children: true });
            this.container = null;
        }

        this.titleText = null;
        this.menuTexts = [];
        this.starGraphics = null;
    }

    getOptions(): Options {
        return this.options;
    }
}
